// ============================================================================
// Session idle detection — pure rules (D-52, 19A §6.5 / 19H §0.7).
//
// The standard has two layers. GoTrue ends a session that has gone 60 minutes
// without a token refresh and one older than 24 hours — but this app runs
// `autoRefreshToken`, so an OPEN dormant tab refreshes its own token forever and
// never trips the server's inactivity timeout. The client therefore carries the
// open-tab half: no pointer / keyboard / visibility activity for (timeout − 2 min)
// → a warning with a live 2:00 countdown → a local sign-out at the timeout.
//
// Everything here is a pure function of numbers, so the thresholds can be tested
// without timers, and an operator's SESSION_INACTIVITY_TIMEOUT (served by
// GET /users/me/sessions/policy) moves the warning with it instead of leaving a
// hard-coded 58 minutes behind.
//
// BYTE-FOR-BYTE THE SAME FILE in admin-dashboard/ and customer-portal/ (and the
// same rules as TPRM7's apps/admin/src/lib/session-idle.ts): the two consoles are
// separate packages with no shared library, so
// backend/tests/unit/session-standard.parity.test.ts fails if the copies drift.
// ============================================================================

/** The modal counts down from this much (19H §0.7: "2:00"). */
export const IDLE_WARNING_LEAD_SECONDS = 120;

/** The D-52 standard, used until (or unless) the server's policy is known. */
export const DEFAULT_INACTIVITY_TIMEOUT_SECONDS = 3600;

/** localStorage key holding the newest activity timestamp seen by ANY tab. */
export const ACTIVITY_STORAGE_KEY = 'vw:last-activity';

export interface IdleThresholds {
  /** Sign out when idle for this long. */
  timeoutMs: number;
  /** Show the warning from this much idle time. */
  warnAtMs: number;
}

/**
 * Thresholds for an inactivity timeout, or null when the detector is OFF.
 * 0 (or anything not a positive finite number) means "no limit", as it does in
 * GoTrue. The warning lead is capped at half the timeout so a very short
 * (test / kiosk) timeout still leaves some quiet time before the warning.
 */
export function idleThresholds(timeoutSeconds: number): IdleThresholds | null {
  if (!Number.isFinite(timeoutSeconds) || timeoutSeconds <= 0) return null;
  const timeoutMs = timeoutSeconds * 1000;
  const leadMs = Math.min(IDLE_WARNING_LEAD_SECONDS * 1000, Math.floor(timeoutMs / 2));
  return { timeoutMs, warnAtMs: timeoutMs - leadMs };
}

export type IdlePhase = 'active' | 'warning' | 'expired';

export function idlePhase(idleMs: number, t: IdleThresholds): IdlePhase {
  if (idleMs >= t.timeoutMs) return 'expired';
  if (idleMs >= t.warnAtMs) return 'warning';
  return 'active';
}

/** Time left before the sign-out, never negative. */
export function remainingMs(idleMs: number, t: IdleThresholds): number {
  return Math.max(0, t.timeoutMs - idleMs);
}

/** m:ss, rounded UP: the display must not read 0:00 while any time remains. */
export function formatCountdown(ms: number): string {
  const total = Math.ceil(Math.max(0, ms) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Idle time, measured from the LATEST activity any tab has seen. Per-tab clocks
 * alone would be wrong in a way that matters: a background tab nobody touched
 * would time out and sign the user out of the whole browser session while they
 * were busy in another tab. A timestamp from the future (the clock was changed)
 * counts as "just now" and an unreadable shared value falls back to this tab's.
 */
export function effectiveIdleMs(nowMs: number, localLastMs: number, sharedLastMs: number | null): number {
  const shared = sharedLastMs !== null && Number.isFinite(sharedLastMs) ? sharedLastMs : -Infinity;
  const last = Math.max(localLastMs, shared);
  return Math.max(0, nowMs - Math.min(last, nowMs));
}

/** The shared (cross-tab) last-activity timestamp, or null if unavailable. */
export function readSharedActivity(): number | null {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_STORAGE_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null; // storage blocked (private window): this tab's own clock still works
  }
}

export function writeSharedActivity(ts: number): void {
  try {
    window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(ts));
  } catch {
    // best-effort: storage blocked, so other tabs cannot see this activity; the
    // worst case is an earlier warning in a tab the user is not looking at.
  }
}
